import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useEventFeedStore } from '@/stores/event-feed.store';
import type { FeedContext } from '@/stores/event-feed.store';
import type { FeedTitleOption } from '../components';
import type { FeedEmptyStateProps } from '../components';

const FIRST_ALTERNATE_INDEX = '1';

const CONTEXT_LABEL_KEY: Record<FeedContext['type'], string> = {
    recentAlerts: 'sidebar.recentAlerts',
    recentEvents: 'sidebar.recentEvents',
    databaseEvents: 'sidebar.databaseEvents',
    sourceDatabaseEvents: 'sidebar.databaseEvents',
    sourceEvents: 'sidebar.sourceEvents',
    sourceAlerts: 'sidebar.sourceAlerts',
    alertRule: 'sidebar.alertRuleEvents',
    alerts: 'sidebar.recentAlerts',
    relatedEvent: 'sidebar.relatedEvents',
    vehicle: 'sidebar.vehicleEvents',
    person: 'sidebar.personEvents',
    queryResults: 'sidebar.queryResults',
    reportEvents: 'sidebar.reportEvents',
};

const CONTEXT_EMPTY_STATE_KEY: Record<FeedContext['type'], string> = {
    recentAlerts: 'emptyState.recentAlerts',
    recentEvents: 'emptyState.recentEvents',
    databaseEvents: 'emptyState.databaseEvents',
    sourceDatabaseEvents: 'emptyState.databaseEvents',
    sourceEvents: 'emptyState.sourceEvents',
    sourceAlerts: 'emptyState.sourceAlerts',
    alertRule: 'emptyState.alerts',
    alerts: 'emptyState.alerts',
    relatedEvent: 'emptyState.relatedEvents',
    vehicle: 'emptyState.vehicleEvents',
    person: 'emptyState.personEvents',
    queryResults: 'emptyState.recentEvents',
    reportEvents: 'emptyState.reportEvents',
};

interface FeedPanelProps {
    context: FeedContext | null;
    hasSwitchableOptions: boolean;
    options: FeedTitleOption[];
    switchFeedContext: (value: string) => void;
    emptyState: FeedEmptyStateProps | undefined;
}

export function useFeedPanelProps(): FeedPanelProps {
    const context = useEventFeedStore((state) => state.context);
    const alternateContexts = useEventFeedStore(
        (state) => state.alternateContexts,
    );
    const show = useEventFeedStore((state) => state.show);
    const { t } = useTranslation(I18nNamespace.EVENTS);

    const allContexts = useMemo(() => {
        if (!context) return [];
        return [context, ...alternateContexts];
    }, [context, alternateContexts]);

    const options: FeedTitleOption[] = useMemo(
        () =>
            allContexts.map((feedContext, index) => ({
                value: String(index),
                label: t(CONTEXT_LABEL_KEY[feedContext.type]),
            })),
        [allContexts, t],
    );

    const switchFeedContext = useCallback(
        (value: string) => {
            const selectedIndex = Number(value);
            const selected = allContexts[selectedIndex];
            if (!selected || selected === context) return;
            const remaining = allContexts.filter((_, i) => i !== selectedIndex);
            show(selected, remaining);
        },
        [allContexts, context, show],
    );

    const hasSwitchableOptions = options.length > 1;

    const emptyState = useMemo<FeedEmptyStateProps | undefined>(() => {
        if (!hasSwitchableOptions || !context || alternateContexts.length === 0)
            return undefined;
        return {
            title: t(`${CONTEXT_EMPTY_STATE_KEY[context.type]}.title`),
            description: t(
                `${CONTEXT_EMPTY_STATE_KEY[context.type]}.description`,
            ),
            action: {
                label: t('emptyState.switchFeed', {
                    feedName: t(CONTEXT_LABEL_KEY[alternateContexts[0].type]),
                }),
                onPress: () => switchFeedContext(FIRST_ALTERNATE_INDEX),
            },
        };
    }, [
        hasSwitchableOptions,
        context,
        alternateContexts,
        switchFeedContext,
        t,
    ]);

    return {
        context,
        hasSwitchableOptions,
        options,
        switchFeedContext,
        emptyState,
    };
}
